import React, { useState, useRef, useCallback, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Form, {
  Item,
  Label,
  ButtonItem,
  ButtonOptions,
  RequiredRule,
} from "devextreme-react/form";
import { toast } from "react-hot-toast";
import LoadIndicator from "devextreme-react/load-indicator";
import {
  Box,
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { Close as CloseIcon } from "@mui/icons-material";
import "./create-category-form.scss";
import { SingleCard } from "../../layouts";
import { useAuth } from "../../contexts/auth";
import { baseAxios } from "../../utils/config";
import { CirclePicker, SliderPicker } from "react-color";

const FLAGS = ["Chemical", "Non Chemical"];
const FORM_ID = "category-dialog-form";

function isUnsetColor(color) {
  const normalized = String(color ?? "").toLowerCase();
  return !normalized || normalized === "#fff" || normalized === "#ffffff";
}

export default function CreateCategoryForm(props = {}) {
  const { categoryId: categoryIdProp, onSaved, onRequestClose } = props;
  const { search } = useLocation();

  const queryParams = new URLSearchParams(search);
  const categoryIdFromQuery = parseInt(queryParams.get("categoryId"), 10);

  const effectiveCategoryId = useMemo(() => {
    if (categoryIdProp != null && categoryIdProp !== "") {
      return Number(categoryIdProp);
    }
    return Number.isFinite(categoryIdFromQuery) ? categoryIdFromQuery : null;
  }, [categoryIdProp, categoryIdFromQuery]);

  const isDialogMode = typeof onRequestClose === "function";

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(!!effectiveCategoryId && isDialogMode);
  const formData = useRef({});
  const [color, setColor] = useState("#fff");
  const [flag, setFlag] = useState("");
  const [name, setName] = useState("");
  const [nameError, setNameError] = useState("");
  const [flagError, setFlagError] = useState("");

  const userCredentials = {
    Barcode: user.barcode,
    Role: user.userRoles,
    Organization: user.org,
    Location: user.location_id,
  };

  const syncLegacyFormData = useCallback((nextName, nextFlag, nextColor) => {
    formData.current.name = nextName;
    formData.current.flag = nextFlag;
    formData.current.color = nextColor;
  }, []);

  useEffect(() => {
    if (!effectiveCategoryId) return;

    const loadCategory = async () => {
      if (isDialogMode) setFetching(true);
      try {
        const res = await baseAxios.get(`/categories/${effectiveCategoryId}`);
        const categoryName = res.data.category_name ?? "";
        const categoryFlag = res.data.flag ?? "";
        const categoryColor = res.data.color ?? "#fff";

        if (isDialogMode) {
          setName(categoryName);
          setFlag(categoryFlag);
          setColor(categoryColor);
        } else {
          formData.current.name = categoryName;
          setFlag(categoryFlag);
          setColor(categoryColor);
          formData.current.flag = categoryFlag;
          formData.current.color = categoryColor;
        }
        syncLegacyFormData(categoryName, categoryFlag, categoryColor);
      } catch {
        toast.error("Could not load category.", { duration: 3000 });
      } finally {
        if (isDialogMode) setFetching(false);
      }
    };

    loadCategory();
  }, [effectiveCategoryId, isDialogMode, syncLegacyFormData]);

  const handleFlagChange = (newFlag) => {
    setFlag(newFlag);
    formData.current.flag = newFlag;
    if (flagError) setFlagError("");
  };

  const handleColorChange = (newColor) => {
    setColor(newColor);
    formData.current.color = newColor;
  };

  const handleNameChange = (event) => {
    const nextName = event.target.value;
    setName(nextName);
    formData.current.name = nextName;
    if (nameError) setNameError("");
  };

  const handleSuccess = useCallback(() => {
    if (typeof onSaved === "function") {
      onSaved();
      return;
    }
    setTimeout(() => {
      navigate(-1);
    }, 1000);
  }, [navigate, onSaved]);

  const validateForm = useCallback(() => {
    let valid = true;
    const trimmedName = String(name).trim();

    if (!trimmedName) {
      setNameError("Please choose a category name");
      valid = false;
    } else {
      setNameError("");
    }

    if (!flag) {
      setFlagError("Please choose a flag");
      valid = false;
    } else {
      setFlagError("");
    }

    if (isUnsetColor(color)) {
      toast.error("Please choose a color", { duration: 5000 });
      valid = false;
    }

    return valid;
  }, [name, flag, color]);

  const submitPayload = useCallback(async () => {
    const payload = isDialogMode
      ? { name: String(name).trim(), flag, color }
      : formData.current;

    if (effectiveCategoryId) {
      const res = await baseAxios.post(
        `categories/update/${effectiveCategoryId}`,
        payload
      );
      if (res.status === 200) {
        toast.success("Category Updated", { duration: 3000 });
        handleSuccess();
      } else {
        toast.error("Category update failed, please try again", {
          duration: 5000,
        });
      }
      return;
    }

    const res = await baseAxios.post("categories/create", payload);
    if (res.status === 200) {
      toast.success("Category Created", { duration: 3000 });
      handleSuccess();
    } else {
      toast.error("Category creation failed, please try again", {
        duration: 5000,
      });
    }
  }, [color, effectiveCategoryId, flag, handleSuccess, isDialogMode, name]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();

      if (
        !userCredentials.Role.includes(6) &&
        !userCredentials.Role.includes(3) &&
        !userCredentials.Role.includes(2)
      ) {
        toast.error("User Not authorized, please check your role", {
          duration: 5000,
        });
        return;
      }

      if (isDialogMode) {
        if (!validateForm()) return;
      } else {
        const { flag: legacyFlag, name: legacyName, color: legacyColor } =
          formData.current;
        if (!legacyName) {
          toast.error("Please choose a category name", { duration: 5000 });
          return;
        }
        if (!legacyFlag) {
          toast.error("Please choose a flag", { duration: 5000 });
          return;
        }
        if (isUnsetColor(legacyColor)) {
          toast.error("Please choose a color", { duration: 5000 });
          return;
        }
      }

      setLoading(true);
      try {
        await submitPayload();
      } catch (error) {
        if (error.status === 400) {
          toast.error(error.data.message, { duration: 5000 });
        } else {
          toast.error("Operation failed: " + error.message, { duration: 5000 });
        }
      } finally {
        setLoading(false);
      }
    },
    [isDialogMode, submitPayload, userCredentials.Role, validateForm]
  );

  const nameEditorOptions = {
    stylingMode: "filled",
    placeholder: "Category Name",
  };

  const flagEditorOptions = {
    items: FLAGS,
    value: flag,
    searchEnabled: false,
    placeholder: "Choose a Flag",
    onValueChanged: ({ value }) => handleFlagChange(value),
  };

  const busy = loading || fetching;

  const legacyFormContent = (
    <form className={"create-category-form"} onSubmit={onSubmit}>
      <Form formData={formData.current} disabled={loading}>
        <Item
          dataField={"name"}
          editorType={"dxTextBox"}
          editorOptions={nameEditorOptions}
        >
          <Label visible={false} />
          <RequiredRule message={"Please choose a category name"} />
        </Item>

        <Item
          dataField={"flag"}
          editorType={"dxSelectBox"}
          editorOptions={flagEditorOptions}
          label={{ visible: false }}
        >
          <RequiredRule message={"Please choose a flag"} />
        </Item>

        <Item itemType="group" colCount={1} caption="Choose a color">
          <Item>
            <Label text="Color" visible={false} />
            <CirclePicker
              color={color}
              onChangeComplete={(c) => handleColorChange(c.hex)}
            />
          </Item>
          <Item>
            <Label text="Color" visible={false} />
            <div style={{ position: "relative" }}>
              <SliderPicker
                color={color}
                onChange={(c) => handleColorChange(c.hex)}
              />
            </div>
          </Item>
        </Item>

        <ButtonItem>
          <ButtonOptions
            width={"100%"}
            type={"default"}
            useSubmitBehavior={true}
          >
            <span className="dx-button-text">
              {loading ? (
                <LoadIndicator width={"24px"} height={"24px"} visible={true} />
              ) : effectiveCategoryId ? (
                "Save category"
              ) : (
                "Create a new category"
              )}
            </span>
          </ButtonOptions>
        </ButtonItem>
      </Form>
    </form>
  );

  if (isDialogMode) {
    return (
      <>
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: 2,
            bgcolor: "secondary.main",
            color: "common.white",
            borderBottom: "1px solid",
            borderColor: "primary.main",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight={700} color="inherit">
              {effectiveCategoryId ? "Update category" : "Add new category"}
            </Typography>
            <Typography
              variant="body2"
              sx={{ color: "rgba(255, 255, 255, 0.75)", mt: 0.25 }}
            >
              {effectiveCategoryId
                ? "Update the category name, flag, and color."
                : "Create a new service category with a flag and color."}
            </Typography>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onRequestClose}
            disabled={busy}
            size="small"
            sx={{
              color: "primary.main",
              mt: 0.25,
              "&:hover": { bgcolor: "rgba(197, 160, 111, 0.16)" },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {fetching ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
              <CircularProgress size={28} color="primary" />
            </Box>
          ) : (
            <Box
              component="form"
              id={FORM_ID}
              onSubmit={onSubmit}
              sx={{ p: 3 }}
            >
              <Stack spacing={2}>
                <TextField
                  label="Category name"
                  value={name}
                  onChange={handleNameChange}
                  required
                  fullWidth
                  size="small"
                  disabled={busy}
                  error={Boolean(nameError)}
                  helperText={nameError}
                  color="primary"
                />

                <FormControl
                  fullWidth
                  size="small"
                  error={Boolean(flagError)}
                  color="primary"
                >
                  <InputLabel id="category-flag-label">Flag</InputLabel>
                  <Select
                    labelId="category-flag-label"
                    label="Flag"
                    value={flag}
                    onChange={(event) => handleFlagChange(event.target.value)}
                    disabled={busy}
                  >
                    {FLAGS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Select>
                  {flagError ? (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, ml: 1.75 }}
                    >
                      {flagError}
                    </Typography>
                  ) : null}
                </FormControl>

                <Box>
                  <Typography
                    variant="subtitle2"
                    fontWeight={700}
                    sx={{ mb: 1 }}
                  >
                    Choose a color
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      mb: 1.5,
                    }}
                  >
                    <Box
                      sx={{
                        width: 28,
                        height: 28,
                        borderRadius: "50%",
                        bgcolor: color,
                        border: "1px solid",
                        borderColor: "divider",
                        flexShrink: 0,
                      }}
                    />
                    <Typography variant="body2" color="text.secondary">
                      {color}
                    </Typography>
                  </Box>
                  <CirclePicker
                    color={color}
                    onChangeComplete={(c) => handleColorChange(c.hex)}
                  />
                  <Box sx={{ mt: 1.5 }}>
                    <SliderPicker
                      color={color}
                      onChange={(c) => handleColorChange(c.hex)}
                    />
                  </Box>
                </Box>
              </Stack>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 3,
            py: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            gap: 1,
          }}
        >
          <Button
            onClick={onRequestClose}
            disabled={busy}
            variant="outlined"
            color="secondary"
            sx={{ textTransform: "none", minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form={FORM_ID}
            variant="contained"
            color="primary"
            disabled={busy || fetching}
            sx={{
              minWidth: 140,
              textTransform: "none",
              fontWeight: 700,
              boxShadow: "none",
              "&:hover": { boxShadow: "none" },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : effectiveCategoryId ? (
              "Save category"
            ) : (
              "Create category"
            )}
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <SingleCard
      title={effectiveCategoryId ? "Update category" : "Add new category"}
    >
      {legacyFormContent}
    </SingleCard>
  );
}
